/**
 * TimerWidget — Floating persistent timer (Step 17)
 */
import { useState, useEffect, useRef, useCallback, useMemo , createElement } from 'react';
import {
  CheckSquare,
  ChevronDown,
  ChevronUp,
  List,
  Code,
  DollarSign,
  FileText,
  Zap,
  FolderKanban,
  MessageSquare,
  Palette,
  Pause,
  Flag,
  Play,
  Search,
  Settings,
  Square,
  Tag,
  Timer,
  Trash2,
  X,
} from 'lucide-react';
import {
  useGetTimerStatusQuery,
  useStartTimerMutation,
  usePauseTimerMutation,
  useResumeTimerMutation,
  useStopTimerMutation,
  useDiscardTimerMutation,
} from '../../api/timeTrackingApi';
import { useListProjectsQuery } from '../../api/projectApi';
import { toast } from '../ui/Feedback';

const CATEGORIES = [
  { value: 'development', label: 'Development', icon: Code },
  { value: 'design', label: 'Design', icon: Palette },
  { value: 'meeting', label: 'Meeting', icon: MessageSquare },
  { value: 'review', label: 'Review', icon: Search },
  { value: 'testing', label: 'Testing', icon: Zap },
  { value: 'documentation', label: 'Docs', icon: FileText },
  { value: 'planning', label: 'Planning', icon: List },
  { value: 'support', label: 'Support', icon: MessageSquare },
  { value: 'admin', label: 'Admin', icon: Settings },
  { value: 'other', label: 'Other', icon: Flag },
];

const formatElapsed = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TimerWidget = () => {
  const [expanded, setExpanded] = useState(false);
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [billable, setBillable] = useState(true);
  const [category, setCategory] = useState('other');
  const [liveElapsed, setLiveElapsed] = useState(0);
  const intervalRef = useRef(null);
  const serverElapsedRef = useRef(0);
  const serverFetchedAtRef = useRef(0);
  const statusRef = useRef('idle');

  const { data: timerRes, refetch: refetchTimer } = useGetTimerStatusQuery(undefined, {
    pollingInterval: 30000,
  });
  const { data: projectsRes } = useListProjectsQuery({ limit: 50 });
  const projects = useMemo(() => projectsRes?.data || [], [projectsRes]);

  const [startTimer, { isLoading: starting }] = useStartTimerMutation();
  const [pauseTimer] = usePauseTimerMutation();
  const [resumeTimer] = useResumeTimerMutation();
  const [stopTimer, { isLoading: stopping }] = useStopTimerMutation();
  const [discardTimer] = useDiscardTimerMutation();

  const timer = timerRes?.data;
  const isActive = timer?.active;
  const isRunning = timer?.status === 'running';
  const isPaused = timer?.status === 'paused';

  useEffect(() => {
    if (timer?.active) {
      serverElapsedRef.current = timer.elapsed || 0;
      serverFetchedAtRef.current = Date.now();
      statusRef.current = timer.status;
      if (timer.description) setDescription(timer.description);
      if (timer.projectId) setProjectId(timer.projectId);
      if (timer.billable !== undefined) setBillable(timer.billable);
      if (timer.category) setCategory(timer.category);
    } else {
      statusRef.current = 'idle';
    }
  }, [timer]);

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        let elapsed = serverElapsedRef.current;
        if (statusRef.current === 'running') {
          elapsed += Date.now() - serverFetchedAtRef.current;
        }
        setLiveElapsed(elapsed);
      }, 1000);
    } else {
      setLiveElapsed(0);
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive]);

  const handleStart = useCallback(async () => {
    try {
      await startTimer({ projectId: projectId || undefined, description, billable, category }).unwrap();
      refetchTimer();
    } catch (err) {
      if (err?.data?.error?.code === 'TIMER_ALREADY_RUNNING') refetchTimer();
      else toast.error(err?.data?.error?.message || 'Failed to start timer');
    }
  }, [projectId, description, billable, category, startTimer, refetchTimer]);

  const handlePause = useCallback(async () => {
    try {
      await pauseTimer().unwrap();
      statusRef.current = 'paused';
      serverElapsedRef.current = liveElapsed;
      serverFetchedAtRef.current = Date.now();
      refetchTimer();
    } catch { toast.error('Failed to pause'); }
  }, [pauseTimer, liveElapsed, refetchTimer]);

  const handleResume = useCallback(async () => {
    try {
      await resumeTimer().unwrap();
      statusRef.current = 'running';
      serverFetchedAtRef.current = Date.now();
      refetchTimer();
    } catch { toast.error('Failed to resume'); }
  }, [resumeTimer, refetchTimer]);

  const handleStop = useCallback(async () => {
    try {
      await stopTimer().unwrap();
      toast.success('Time entry saved');
      setDescription('');
      setProjectId('');
      setCategory('other');
      refetchTimer();
    } catch (err) { toast.error(err?.data?.error?.message || 'Failed to stop timer'); }
  }, [stopTimer, refetchTimer]);

  const handleDiscard = useCallback(async () => {
    try { await discardTimer().unwrap(); setDescription(''); refetchTimer(); } catch { /* ignore */ }
  }, [discardTimer, refetchTimer]);

  const statusColor = isRunning ? '#10b981' : isPaused ? '#f59e0b' : '#1338be';

  return (
    <div className="fixed bottom-5 right-5 z-[9990]">
      {isActive && !expanded && (
        <button onClick={() => setExpanded(true)}
          className="flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-full shadow-lg border border-white/30 backdrop-blur-lg transition-all hover:scale-105 active:scale-95"
          style={{ background: `linear-gradient(135deg, ${statusColor}20, ${statusColor}08)`, boxShadow: `0 4px 24px ${statusColor}25, 0 0 0 1px ${statusColor}15` }}>
          <span className="relative flex h-2.5 w-2.5">
            {isRunning && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: statusColor }} />}
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: statusColor }} />
          </span>
          <span className="text-[13px] font-mono font-bold tabular-nums" style={{ color: statusColor }}>{formatElapsed(liveElapsed)}</span>
          <ChevronUp size={12} className="text-surface-500" />
        </button>
      )}

      {!isActive && !expanded && (
        <button onClick={() => setExpanded(true)}
          className="flex items-center gap-2 p-3 rounded-full bg-primary-500 text-white shadow-lg hover:bg-primary-600 hover:scale-105 active:scale-95 transition-all" title="Start timer">
          <Timer size={18} />
        </button>
      )}

      {expanded && (
        <div className="w-[320px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-dropdown border border-surface-100 overflow-hidden animate-modal-content">
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="relative">
                  {isRunning && <span className="absolute -inset-1 rounded-full animate-ping opacity-20" style={{ background: statusColor }} />}
                  <Timer size={16} style={{ color: statusColor }} />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: statusColor }}>
                  {isRunning ? 'Running' : isPaused ? 'Paused' : 'Timer'}
                </span>
              </div>
              <button onClick={() => setExpanded(false)} className="p-1 rounded-lg hover:bg-surface-100 text-surface-500">
                <ChevronDown size={14} />
              </button>
            </div>
            <div className="text-center py-2">
              <span className="text-[32px] font-mono font-bold tabular-nums tracking-tight" style={{ color: isActive ? statusColor : '#374151' }}>
                {formatElapsed(liveElapsed)}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 pb-2">
              {!isActive && (
                <button onClick={handleStart} disabled={starting}
                  className="flex items-center gap-1.5 px-5 py-2 bg-emerald-500 text-white rounded-xl text-[12px] font-bold hover:bg-emerald-600 shadow-sm transition-all disabled:opacity-50">
                  <Play size={14} /> Start
                </button>
              )}
              {isRunning && (
                <>
                  <button onClick={handlePause} className="flex items-center gap-1 px-4 py-2 bg-amber-500 text-white rounded-xl text-[12px] font-bold hover:bg-amber-600 shadow-sm transition-all">
                    <Pause size={14} /> Pause
                  </button>
                  <button onClick={handleStop} disabled={stopping} className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-xl text-[12px] font-bold hover:bg-red-600 shadow-sm transition-all disabled:opacity-50">
                    <Square size={12} /> Stop
                  </button>
                </>
              )}
              {isPaused && (
                <>
                  <button onClick={handleResume} className="flex items-center gap-1 px-4 py-2 bg-emerald-500 text-white rounded-xl text-[12px] font-bold hover:bg-emerald-600 shadow-sm transition-all">
                    <Play size={14} /> Resume
                  </button>
                  <button onClick={handleStop} disabled={stopping} className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-xl text-[12px] font-bold hover:bg-red-600 shadow-sm transition-all disabled:opacity-50">
                    <Square size={12} /> Stop
                  </button>
                </>
              )}
              {isActive && (
                <button onClick={handleDiscard} className="p-2 text-surface-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Discard">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="border-t border-surface-100 px-4 py-3 space-y-2.5 bg-surface-50/30">
            <input type="text" placeholder="What are you working on?" value={description} onChange={(e) => setDescription(e.target.value)} disabled={isActive}
              className="w-full text-[12px] bg-white border border-surface-200 rounded-xl px-3 py-2 placeholder-surface-400 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 transition-all disabled:opacity-60 disabled:bg-surface-50" />
            <div className="flex items-center gap-2">
              <FolderKanban size={12} className="text-surface-500 flex-shrink-0" />
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={isActive}
                className="flex-1 text-[11px] bg-white border border-surface-200 rounded-lg px-2 py-1.5 text-surface-600 focus:ring-2 focus:ring-primary-200 disabled:opacity-60 disabled:bg-surface-50">
                <option value="">No project</option>
                {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Tag size={12} className="text-surface-500 flex-shrink-0" />
              <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={isActive}
                className="flex-1 text-[11px] bg-white border border-surface-200 rounded-lg px-2 py-1.5 text-surface-600 disabled:opacity-60 disabled:bg-surface-50">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button onClick={() => !isActive && setBillable(!billable)} disabled={isActive}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${billable ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-surface-50 text-surface-500 border-surface-200'} disabled:opacity-60`}>
                <DollarSign size={10} />
                {billable ? '$' : '—'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimerWidget;
